// apps/admin-ui/src/hooks/useVisibleWidgets.ts
// Filter dashboard widgets based on user's RBAC permissions
// ALL RIGHTS RESERVED

/**
 * FIXED 2026-02-01: Infinite re-render loop
 * - Now uses JSON.stringify for stable memoization of roles array
 */

import { useMemo } from 'react';
import { getCurrentEmployee } from '../utils/auth';
import { hasPermission, type Role } from '../utils/rbac';
import { 
  DASHBOARD_WIDGETS, 
  getWidgetsByPriority,
  type WidgetConfig 
} from '../config/dashboardWidgets';

// =============================================================================
// TYPES
// =============================================================================

export interface UseVisibleWidgetsResult {
  widgets: WidgetConfig[];
  widgetCount: number;
  hasWidget: (widgetId: string) => boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Returns only the dashboard widgets the current user has permission to view.
 * Widgets are sorted by priority and filtered by RBAC permissions.
 * 
 * Roles (12 total):
 *   admin, council, board_director, compliance, treasury, accounting, 
 *   security_admin, contract_auditor, cpa_auditor, custodian, csr, csr_supervisor
 */
export function useVisibleWidgets(): UseVisibleWidgetsResult {
  const employee = getCurrentEmployee();
  
  // FIX: Create a stable string representation of roles for memoization
  // This prevents re-renders when getCurrentEmployee() returns a new object reference
  const rolesKey = useMemo(() => {
    if (!employee?.roles) return '';
    return JSON.stringify(employee.roles.slice().sort());
  }, [employee?.roles?.join(',')]); // Use join() for primitive comparison

  const widgets = useMemo(() => {
    if (!rolesKey) {
      return [];
    }

    const roles = employee?.roles as Role[];
    if (!roles || roles.length === 0) {
      return [];
    }

    // Filter widgets by permission
    const visibleWidgets = DASHBOARD_WIDGETS.filter(widget =>
      hasPermission(roles, widget.permission)
    );

    // Sort by priority
    return getWidgetsByPriority(visibleWidgets);
  }, [rolesKey, employee?.roles]);

  const hasWidget = useMemo(() => {
    const widgetIds = new Set(widgets.map(w => w.id));
    return (widgetId: string) => widgetIds.has(widgetId);
  }, [widgets]);

  return {
    widgets,
    widgetCount: widgets.length,
    hasWidget,
  };
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get widgets for a specific set of roles (useful for previewing)
 */
export function getWidgetsForRoles(roles: Role[]): WidgetConfig[] {
  const visibleWidgets = DASHBOARD_WIDGETS.filter(widget =>
    hasPermission(roles, widget.permission)
  );
  return getWidgetsByPriority(visibleWidgets);
}

/**
 * Get widget count per role (for admin stats)
 * 
 * UPDATED 2026-02-01:
 *   - Added board_director, cpa_auditor, custodian
 */
export function getWidgetCountByRole(): Record<Role, number> {
  const roles: Role[] = [
    'csr',
    'csr_supervisor',
    'compliance',
    'contract_auditor',
    'admin',
    'treasury',
    'council',
    'board_director',
    'security_admin',
    'accounting',
    'cpa_auditor',
    'custodian',
  ];

  const counts: Record<string, number> = {};
  for (const role of roles) {
    counts[role] = getWidgetsForRoles([role]).length;
  }
  return counts as Record<Role, number>;
}

export default useVisibleWidgets;
